import type { PublicPageBrand } from "./public-brand-theme";
import {
  buildMapsHrefForLocation,
  buildSiteConfigForLocation,
  getLocationBySlug,
  isDefaultLocation as isDefaultPlatformLocation,
  resolveLocationByHost,
  resolveLocationByPathname,
  restaurantPlatform,
  type PlatformLocation,
  type RestaurantSiteConfig,
} from "./restaurant-platform";
import { shouldApplyRomaDarkFunnel } from "./roma-funnel-tokens";
import { getBusinessVerticalForRestaurant } from "./tenant-capabilities";
import { isLoyaltyProgramEnabled } from "../../convex/lib/loyaltyProgram";
import {
  resolvePublicLocationCookie,
  shouldUseRememberedPublicLocationPath,
} from "./public-location-cookie";

export type PublicChromeLinkKind =
  | "menu"
  | "events"
  | "private-events"
  | "catering"
  | "contact"
  | "reservation"
  | "consultation"
  | "appointment"
  | "services"
  | "people"
  | "order"
  | "club"
  | "account"
  | "reviews"
  | "jobs"
  | "directions"
  | "about";

export type PublicChromeLink = {
  kind: PublicChromeLinkKind;
  href: string;
  label: string;
  external: boolean;
};

export type PublicChromeSocialPlatform =
  | "instagram"
  | "facebook"
  | "yelp"
  | "x"
  | "youtube"
  | "linkedin"
  | "pinterest"
  | "tiktok"
  | "google";

export type PublicChromeSocialLink = {
  platform: PublicChromeSocialPlatform;
  href: string;
  label: string;
  external: true;
};

export type PublicChromeOptions = {
  /** Server-resolved brand; keeps nav/footer on Roma when dark funnel is on. */
  pageBrand?: PublicPageBrand;
  /** Server-resolved funnel flag; avoids client env mismatch stripping dark chrome. */
  romaFunnelEnabled?: boolean;
};

export type PublicChromeData = {
  location: PlatformLocation;
  siteConfig: RestaurantSiteConfig;
  isDefaultLocation: boolean;
  brandLabel: string;
  brandHref: string;
  brandLogoSrc: string;
  mapsHref: string;
  displayPhone: string;
  hoursLines: string[];
  desktopNavLinks: PublicChromeLink[];
  mobileActionLinks: PublicChromeLink[];
  mobileExploreLinks: PublicChromeLink[];
  mobileSecondaryLinks: PublicChromeLink[];
  footerExploreLinks: PublicChromeLink[];
  footerInfoLinks: PublicChromeLink[];
  socialLinks: PublicChromeSocialLink[];
  showSouthernHighlandsGuides: boolean;
  /**
   * The signed-in-or-not loyalty account entry point. Deliberately kept out
   * of the other link lists (desktopNavLinks, footerInfoLinks, ...) rather
   * than appended to one of them: those arrays are exact-matched in existing
   * chrome tests per location, and account access is not location-specific
   * the way "Catering" or "Careers" are. Undefined for verticals with no
   * "orders" module (loyalty rides on the same module as ordering) AND for any
   * restaurant whose loyalty program is switched off in
   * convex/lib/loyaltyProgram.ts, so navigation.tsx can render it only when it
   * exists instead of the chrome layer needing to know about auth state.
   */
  accountLink?: PublicChromeLink;
};

/**
 * The "My Account" entry, or nothing at all when this restaurant's loyalty
 * program is switched off (convex/lib/loyaltyProgram.ts).
 *
 * The switch is read from the LOCATION being rendered, not from build-time env:
 * one host-aware build serves every restaurant, so Chef Roma must not inherit a
 * nav entry from Tuscan Cove. Fail-closed, since `isLoyaltyProgramEnabled`
 * treats an unknown id as off.
 */
function loyaltyAccountLink(restaurantId: string): PublicChromeLink | undefined {
  if (!isLoyaltyProgramEnabled(restaurantId)) return undefined;
  return link("account", "My Account", "/account") ?? undefined;
}

function link(
  kind: PublicChromeLinkKind,
  label: string,
  href: string | undefined,
): PublicChromeLink | null {
  if (!href) {
    return null;
  }

  return {
    kind,
    label,
    href,
    external: href.startsWith("http"),
  };
}

function compactLinks(links: Array<PublicChromeLink | null>): PublicChromeLink[] {
  return links.filter((item): item is PublicChromeLink => Boolean(item));
}

function socialLink(
  platform: PublicChromeSocialPlatform,
  label: string,
  href: string | undefined,
): PublicChromeSocialLink | null {
  if (!href) {
    return null;
  }

  return {
    platform,
    label,
    href,
    external: true,
  };
}

function compactSocialLinks(
  links: Array<PublicChromeSocialLink | null>,
): PublicChromeSocialLink[] {
  return links.filter((item): item is PublicChromeSocialLink => Boolean(item));
}

function configuredHost(env: Partial<NodeJS.ProcessEnv>): string | undefined {
  return (
    env.NEXT_PUBLIC_SITE_URL ??
    env.NEXT_PUBLIC_APP_URL ??
    env.SITE_URL ??
    getLocationBySlug(restaurantPlatform.defaultLocationId).defaultBaseUrl
  );
}

function resolveLocationForChrome(
  pathname: string | undefined,
  env: Partial<NodeJS.ProcessEnv>,
): PlatformLocation {
  const siteHost = configuredHost(env);
  const rememberedLocation = shouldUseRememberedPublicLocationPath(pathname)
    ? resolvePublicLocationCookie(env.NEXT_PUBLIC_LOCATION_ID)
    : null;

  return (
    resolveLocationByPathname(pathname) ??
    rememberedLocation ??
    resolveLocationByHost(siteHost)
  );
}

function formatTime(time: string): string {
  const [hourPart, minutePart = "00"] = time.split(":");
  const hour = Number(hourPart);
  const minute = Number(minutePart);

  if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
    return time;
  }

  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const displayMinute = minute === 0 ? "" : `:${String(minute).padStart(2, "0")}`;

  return `${displayHour}${displayMinute} ${period}`;
}

function formatDayRange(days: string[]): string {
  const dayMap: Record<string, string> = {
    Monday: "Mon",
    Tuesday: "Tue",
    Wednesday: "Wed",
    Thursday: "Thu",
    Friday: "Fri",
    Saturday: "Sat",
    Sunday: "Sun",
  };

  if (days.length === 1) {
    return dayMap[days[0] ?? ""] ?? days[0] ?? "";
  }

  const first = dayMap[days[0] ?? ""] ?? days[0];
  const last = dayMap[days[days.length - 1] ?? ""] ?? days[days.length - 1];

  return `${first}-${last}`;
}

function formatPhone(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  const normalized = digits.length === 11 && digits.startsWith("1")
    ? digits.slice(1)
    : digits;

  if (normalized.length !== 10) {
    return phone;
  }

  return `(${normalized.slice(0, 3)}) ${normalized.slice(3, 6)}-${normalized.slice(6)}`;
}

function buildHoursLines(location: PlatformLocation): string[] {
  return location.hours.weekly.map(
    (entry) =>
      `${formatDayRange(entry.days)} ${formatTime(entry.opens)}-${formatTime(entry.closes)}`,
  );
}

function buildSocialLinks(location: PlatformLocation): PublicChromeSocialLink[] {
  return compactSocialLinks([
    socialLink("instagram", "Instagram", location.socials.instagram),
    socialLink("facebook", "Facebook", location.socials.facebook),
    socialLink("google", "Google", location.socials.googleBusinessProfile),
    socialLink("yelp", "Yelp", location.yelpReservationUrl),
    socialLink("x", "X", location.socials.x),
    socialLink("youtube", "YouTube", location.socials.youtube),
    socialLink("linkedin", "LinkedIn", location.socials.linkedin),
    socialLink("pinterest", "Pinterest", location.socials.pinterest),
    socialLink("tiktok", "TikTok", location.socials.tiktok),
  ]);
}

function buildDefaultChrome(
  location: PlatformLocation,
  siteConfig: RestaurantSiteConfig,
): Omit<
  PublicChromeData,
  | "location"
  | "siteConfig"
  | "isDefaultLocation"
  | "mapsHref"
  | "displayPhone"
  | "hoursLines"
  | "socialLinks"
> {
  void siteConfig;

  return {
    brandLabel: "Tuscan Cove",
    brandHref: "/",
    brandLogoSrc: "/android-chrome-512x512.png",
    desktopNavLinks: compactLinks([
      link("menu", "Menu", "/menu"),
      link("events", "Events", "/events"),
      link("private-events", "Private Events", "/private-events"),
      link("catering", "Catering", "/catering"),
      link("contact", "Contact", "/contact"),
    ]),
    mobileActionLinks: compactLinks([
      link("menu", "View Menu", "/menu"),
      link("reservation", "Reserve a Table", "/reservations"),
      link("order", "Order Online", "/order"),
    ]),
    mobileExploreLinks: compactLinks([
      link("events", "Events", "/events"),
      link("club", "Cove Club", "/cove-club"),
      link("private-events", "Private Events", "/private-events"),
      link("catering", "Catering", "/catering"),
      link("contact", "Contact", "/contact"),
    ]),
    mobileSecondaryLinks: compactLinks([
      link("about", "About", "/about"),
    ]),
    footerExploreLinks: compactLinks([
      link("menu", "Menu", location.menuUrl),
      link("events", "Events", "/events"),
      link("private-events", "Private Events", "/private-events"),
      link("catering", "Catering", "/catering"),
    ]),
    footerInfoLinks: compactLinks([
      link("club", "Cove Club", "/cove-club"),
      link("contact", "Contact Us", "/contact"),
      link("about", "About Us", "/about"),
      link("reviews", "Reviews", "/reviews"),
      link("jobs", "Careers", "/jobs"),
    ]),
    showSouthernHighlandsGuides: true,
    accountLink: loyaltyAccountLink(location.restaurantId),
  };
}

function buildLocationChrome(
  location: PlatformLocation,
  siteConfig: RestaurantSiteConfig,
  mapsHref: string,
): Omit<
  PublicChromeData,
  | "location"
  | "siteConfig"
  | "isDefaultLocation"
  | "mapsHref"
  | "displayPhone"
  | "hoursLines"
  | "socialLinks"
> {
  void siteConfig;

  const isFiaLocation = location.slug === "fia-restaurant-santa-monica";
  const isRomaLocation = location.restaurantId === "roma-kitchen";
  const reservationHref = isRomaLocation
    ? "/reservations#roma-reservation-widget"
    : location.yelpReservationUrl || location.reservationUrl;
  const orderHref = location.orderUrl || location.ordering.orderUrl;
  const privateEventsHref = isFiaLocation
    ? location.privatePartiesUrl
    : "/private-events";
  const cateringHref = isFiaLocation ? location.cateringUrl : "/catering";
  const jobsHref = isFiaLocation ? location.jobsUrl : "/jobs";
  const contactHref = isFiaLocation
    ? `${location.defaultBaseUrl}/visit`
    : "/contact";

  return {
    brandLabel: location.slug === "roma-kitchen-southern-highlands"
      ? "Chef's Roma Kitchen"
      : location.name,
    // Roma's logo must point home, not at /locations/<slug> (a canonicalized
    // duplicate: 200 + canonical=/ + absent from sitemap), to avoid an equity
    // leak. restaurantId covers both the SH and Henderson Roma locations.
    brandHref:
      location.restaurantId === "roma-kitchen"
        ? "/"
        : `/locations/${location.slug}`,
    brandLogoSrc: location.logoImage,
    desktopNavLinks: compactLinks([
      link("menu", "Menu", location.menuUrl),
      link("events", "Events", isRomaLocation ? "/events" : undefined),
      link("private-events", "Private Events", privateEventsHref),
      link("catering", "Catering", cateringHref),
      link("contact", "Contact", contactHref),
    ]),
    mobileActionLinks: compactLinks([
      link("menu", "View Menu", location.menuUrl),
      link("reservation", "Reserve a Table", reservationHref),
      link("order", "Order Online", orderHref),
    ]),
    mobileExploreLinks: compactLinks([
      link("events", "Events", isRomaLocation ? "/events" : undefined),
      link("private-events", "Private Events", privateEventsHref),
      link("catering", "Catering", cateringHref),
    ]),
    mobileSecondaryLinks: compactLinks([
      link("contact", "Contact", contactHref),
    ]),
    footerExploreLinks: compactLinks([
      link("menu", "Menu", location.menuUrl),
      link("reservation", "Reserve", reservationHref),
      link("order", "Order pickup", orderHref),
      link("private-events", "Private Parties", privateEventsHref),
      link("catering", "Catering", cateringHref),
    ]),
    footerInfoLinks: compactLinks([
      link("contact", "Contact", contactHref),
      // Roma's /reviews page was orphaned (0 internal links). Surface it in the
      // footer so it is crawlable and passes equity. Roma-only: other locations
      // have no /reviews page, so the href stays undefined and compactLinks drops it.
      link("reviews", "Reviews", isRomaLocation ? "/reviews" : undefined),
      link("jobs", "Careers", jobsHref),
      link("directions", "Directions", mapsHref),
    ]),
    // Roma surfaces its 4 /best money-page guides sitewide in the footer (they
    // were previously linked only once, from a homepage card). guideLinks is
    // empty for Fia, so the section still renders for Roma alone.
    showSouthernHighlandsGuides: isRomaLocation,
    accountLink: loyaltyAccountLink(location.restaurantId),
  };
}

function buildGenericBusinessChrome(
  location: PlatformLocation,
  siteConfig: RestaurantSiteConfig,
  mapsHref: string,
): Omit<
  PublicChromeData,
  | "location"
  | "siteConfig"
  | "isDefaultLocation"
  | "mapsHref"
  | "displayPhone"
  | "hoursLines"
  | "socialLinks"
> {
  const vertical = getBusinessVerticalForRestaurant(location.restaurantId);
  const primary = vertical.publicChrome;
  const contactHref = "/contact";
  const jobsHref = location.jobsUrl || "/jobs";

  void siteConfig;

  return {
    brandLabel: location.name,
    brandHref: isDefaultPlatformLocation(location)
      ? "/"
      : `/locations/${location.slug}`,
    brandLogoSrc: location.logoImage || "/android-chrome-512x512.png",
    desktopNavLinks: compactLinks([
      link("services", primary.serviceLabel, primary.serviceHref),
      link("people", primary.peopleLabel, primary.peopleHref),
      link("contact", "Contact", contactHref),
    ]),
    mobileActionLinks: compactLinks([
      link(
        primary.primaryActionKind,
        primary.primaryActionLabel,
        primary.primaryActionHref,
      ),
      link("directions", "Directions", mapsHref),
    ]),
    mobileExploreLinks: compactLinks([
      link("services", primary.serviceLabel, primary.serviceHref),
      link("people", primary.peopleLabel, primary.peopleHref),
    ]),
    mobileSecondaryLinks: compactLinks([
      link("contact", "Contact", contactHref),
    ]),
    footerExploreLinks: compactLinks([
      link(
        primary.primaryActionKind,
        primary.primaryActionLabel,
        primary.primaryActionHref,
      ),
      link("services", primary.serviceLabel, primary.serviceHref),
      link("people", primary.peopleLabel, primary.peopleHref),
    ]),
    footerInfoLinks: compactLinks([
      link("contact", "Contact", contactHref),
      link("jobs", "Careers", jobsHref),
      link("directions", "Directions", mapsHref),
    ]),
    showSouthernHighlandsGuides: false,
  };
}

export function getPublicChromeForPathname(
  pathname: string | undefined,
  env: Partial<NodeJS.ProcessEnv> = process.env,
  options?: PublicChromeOptions,
): PublicChromeData {
  const mergedEnv = { ...process.env, ...env };
  let location = resolveLocationForChrome(pathname, mergedEnv);

  const romaFunnelActive =
    options?.pageBrand === "chefs-roma" &&
    (options?.romaFunnelEnabled ??
      shouldApplyRomaDarkFunnel(options.pageBrand, mergedEnv));

  if (romaFunnelActive) {
    location = getLocationBySlug("roma-kitchen-southern-highlands");
  }
  const isDefaultLocation = isDefaultPlatformLocation(location);
  const siteConfig = buildSiteConfigForLocation(location, {
    baseUrl: isDefaultLocation ? configuredHost(mergedEnv) : location.defaultBaseUrl,
    env: mergedEnv,
  });
  const mapsHref = buildMapsHrefForLocation(location);
  const vertical = getBusinessVerticalForRestaurant(location.restaurantId);
  const baseChrome = vertical.id !== "restaurant"
    ? buildGenericBusinessChrome(location, siteConfig, mapsHref)
    : isDefaultLocation
      ? buildDefaultChrome(location, siteConfig)
      : buildLocationChrome(location, siteConfig, mapsHref);

  return {
    location,
    siteConfig,
    isDefaultLocation,
    mapsHref,
    displayPhone: formatPhone(siteConfig.contact.phone),
    hoursLines: buildHoursLines(location),
    socialLinks: buildSocialLinks(location),
    ...baseChrome,
  };
}
